import { ChartBarDataSchema, ChartBarResponse, ChartBarUISchema } from './types'

export function isRenderableChartBarResponse(
  response: ChartBarResponse
): response is ChartBarResponse & { uiSchema: ChartBarUISchema; dataSchema: ChartBarDataSchema } {
  if (response.emptyResponse) {
    return false
  }

  const chartConfig = response.uiSchema?.chartConfig
  if (!chartConfig?.barDataKeys?.length || !chartConfig.categoryKey) {
    return false
  }

  return Array.isArray(response.dataSchema?.data)
}

export function isChartBarResponseEmpty(response: ChartBarResponse): boolean {
  return !isRenderableChartBarResponse(response)
}

export function hasChartBarData(dataSchema: ChartBarDataSchema | undefined): boolean {
  return (dataSchema?.data?.length ?? 0) > 0
}

export function computeBarChartDomain(
  data: ChartBarDataSchema['data'],
  barDataKeys: string[],
  stacked: boolean | undefined,
  getStackId: (key: string) => string | undefined
): [number, number] {
  if (data.length === 0) {
    return [0, 0]
  }

  let domainMin = 0
  let domainMax = 0

  if (stacked) {
    for (const item of data) {
      const stackSums = new Map<string | undefined, { positive: number; negative: number }>()
      barDataKeys.forEach((key) => {
        const stackId = getStackId(key)
        const value = Number(item[key]) || 0
        const sums = stackSums.get(stackId) || { positive: 0, negative: 0 }
        if (value >= 0) {
          sums.positive += value
        } else {
          sums.negative += value
        }
        stackSums.set(stackId, sums)
      })

      for (const sums of stackSums.values()) {
        domainMin = Math.min(domainMin, sums.negative)
        domainMax = Math.max(domainMax, sums.positive)
      }
    }

    return [domainMin, domainMax]
  }

  for (const item of data) {
    for (const key of barDataKeys) {
      const value = Number(item[key]) || 0
      domainMin = Math.min(domainMin, value)
      domainMax = Math.max(domainMax, value)
    }
  }

  return [domainMin, domainMax]
}

export function generateDataAxisTicks(domainMin: number, domainMax: number, tickCount = 5): number[] {
  if (domainMin === domainMax) return [domainMin]

  const roughInterval = (domainMax - domainMin) / tickCount
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)))
  const normalizedInterval = roughInterval / magnitude

  let niceInterval: number
  if (normalizedInterval <= 1) {
    niceInterval = magnitude
  } else if (normalizedInterval <= 2) {
    niceInterval = 2 * magnitude
  } else if (normalizedInterval <= 5) {
    niceInterval = 5 * magnitude
  } else {
    niceInterval = 10 * magnitude
  }

  const decimalPlaces = niceInterval >= 1 ? 0 : Math.ceil(-Math.log10(niceInterval))
  const firstTick = Math.floor(domainMin / niceInterval) * niceInterval
  const lastTick = Math.ceil(domainMax / niceInterval) * niceInterval
  const tickCountWithBounds = Math.round((lastTick - firstTick) / niceInterval)

  return Array.from({ length: tickCountWithBounds + 1 }, (_, index) =>
    parseFloat((firstTick + index * niceInterval).toFixed(decimalPlaces))
  )
}
